import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Caption from '@zenbusiness/common-components/Caption';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import PropTypes from 'prop-types';
import { useParams, Link as RouterLink } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

import { readLastUpdatedDate } from '../../utils/date';

const StyledCardsTitle = styled(Grid)(({ theme }) => ({
  order: 1,
  marginRight: theme.spacing(2)
}));

const StyledCardsStatus = styled(Grid)(({ theme }) => ({
  order: 3,
  flexBasis: '100%',

  [theme.breakpoints.up('md')]: {
    order: 2,
    flexBasis: 'auto'
  }
}));

const StyledCardHeaderGrid = styled(Grid)(({ theme }) => ({
  height: '62px',
  [theme.breakpoints.up('md')]: {
    height: '39px'
  }
}));

const StyledContactInfoGrid = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(1)
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  height: 1,
  background: theme.palette.focus30.main
}));

const StyledInnerCard = styled(Card)(({ theme }) => ({
  overflow: 'visible',
  width: '100%',
  '& .MuiCardContent-root': {
    padding: theme.spacing(0.5),
    height: 'auto',
    [theme.breakpoints.up('md')]: {
      height: '235px'
    }
  },
  '& .MuiCardActions-root': {
    padding: theme.spacing(1, 0.5),
    minHeight: 'auto',
    [theme.breakpoints.up('md')]: {
      minHeight: '53px'
    }
  },
  '& .MuiDivider-root': {
    backgroundColor: '#f5f9fa'
  }
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  justifyContent: 'right',
  marginRight: theme.spacing(-2)
}));

const StyledText = styled(Text)(({ theme, disabled }) => ({
  ...(disabled && { color: theme.palette.focus30.main })
}));

const messages = {
  type: {
    admin: 'Administrative Contact',
    owner: 'Owner Contact',
    tech: 'Technical & Billing Contact'
  }
};

const contactTypeOrder = [
  'owner',
  'admin',
  'tech'
];

const ContactCards = ({
  domain
}) => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid } = useParams();
  const lastUpdated = readLastUpdatedDate(domain.domainContacts);
  const { domainContacts } = domain;

  let contactCards;

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const handleEditClick = useLinkPressedCallback({
    elementName: 'edit_contact',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  if (!domainContacts || domainContacts.length <= 0) {
    contactCards = (<></>);
  } else {
    contactCards = domainContacts
      .filter(({ contactType }) => (contactTypeOrder.includes(contactType)))
      .sort((c1, c2) => (
        contactTypeOrder.indexOf(c1.contactType) - contactTypeOrder.indexOf(c2.contactType)
      ))
      .map((contact) => (
        <Grid
          key={contact.domainContactUuid}
          item
          xs={12}
        >

          <StyledInnerCard elevation={0} xs={12}>
            <Grid container item xs={12} direction="row">
              <Grid item xs={6}>
                <CardContent>

                  <Grid container xs={12} item spacing={2} direction="column">
                    <Grid item>
                      <Title level={6} variant="sm">
                        {messages.type[contact.contactType]}
                      </Title>
                    </Grid>
                    <Grid item>
                      {!contact.editable && <Text color="focus70" size="sm">Locked</Text> }
                      {contact.editable && <Text color="focus70" size="sm">{domain.domainPrivacy ? 'Private' : 'Public'}</Text>}
                    </Grid>
                  </Grid>

                  <StyledContactInfoGrid item>
                    <StyledText size="lg" disabled={!contact.editable}>
                      {`${contact.firstName} ${contact.lastName}`}
                      <br />
                      {contact.address1}
                      <br />
                      {contact.address2 && (
                        <>
                          {contact.address2}
                          <br />
                        </>
                      )}
                      {`${contact.city}, ${contact.state}, ${contact.zipCode}`}
                      <br />
                      {contact.phone && (
                        <>
                          {contact.phone}
                          <br />
                        </>
                      )}
                      {contact.email && (
                        <>
                          {contact.email}
                        </>
                      )}
                    </StyledText>
                  </StyledContactInfoGrid>
                </CardContent>
              </Grid>
              <Grid item xs={6}>
                <StyledCardActions>
                  {contact.editable && (
                    <Button size="small" variant="text" color="primary" onClick={handleEditClick} disabled={!contact.editable} sx={{ textTransform: 'none' }} component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/contacts/${contact.domainContactUuid}`}>Edit</Button>
                  )}
                </StyledCardActions>

              </Grid>
            </Grid>
          </StyledInnerCard>
        </Grid>
      ));
  }

  return (
    <Grid id="contacts">
      <Grid item>
        <StyledCardHeaderGrid
          xs={12}
          container
          item
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-end"
        >
          <StyledCardsTitle item>
            <Text size="md">Contacts</Text>
          </StyledCardsTitle>
          {lastUpdated && (
            <StyledCardsStatus item>
              <Caption size="lg" color="focus50">
                {`Last updated: ${lastUpdated}`}
              </Caption>
            </StyledCardsStatus>
          )}
        </StyledCardHeaderGrid>
        <StyledDivider />
        <Grid
          container
          item
          xs={12}
          direction="column"
          justifyContent="flex-start"
        >
          {contactCards}
        </Grid>
      </Grid>
    </Grid>
  );
};

/* eslint-disable react/forbid-prop-types */
ContactCards.propTypes = {
  domain: PropTypes.object.isRequired
};
/* eslint-enable react/forbid-prop-types */

export default ContactCards;
